import { z } from "zod";
import type { Args, Operation } from "../../types";
import { scoped } from "../../types";
import { crmIdParams, crmOpportunityRowSchema } from "./_shared";

const oppList: Operation<
  void,
  {
    pipeline_id?: number;
    stage_id?: number;
    lead_id?: number;
    owner_actor_id?: number;
    closed_won?: boolean;
    closed_lost?: boolean;
  },
  void,
  { opportunities: z.infer<typeof crmOpportunityRowSchema>[] }
> = {
  method: "GET",
  path: () => "/api/crm/opportunities",
  query: z
    .object({
      pipeline_id: z.coerce.number().int().optional(),
      stage_id: z.coerce.number().int().optional(),
      lead_id: z.coerce.number().int().optional(),
      owner_actor_id: z.coerce.number().int().optional(),
      closed_won: z.coerce.boolean().optional(),
      closed_lost: z.coerce.boolean().optional(),
    })
    .partial(),
  response: z.object({ opportunities: z.array(crmOpportunityRowSchema) }),
};

const create: Operation<
  void,
  void,
  {
    lead_id: number;
    name: string;
    value_minor?: number;
    currency?: string;
    pipeline_id?: number;
    stage_id?: number;
    probability_pct?: number | null;
    expected_close_date?: string | null;
    owner_actor_id?: number | null;
  },
  z.infer<typeof crmOpportunityRowSchema>
> = {
  method: "POST",
  path: () => "/api/crm/opportunities",
  body: z.object({
    lead_id: z.number().int(),
    name: z.string().min(1),
    value_minor: z.number().int().optional(),
    currency: z.string().length(3).optional(),
    pipeline_id: z.number().int().optional(),
    stage_id: z.number().int().optional(),
    probability_pct: z.number().int().nullable().optional(),
    expected_close_date: z.string().nullable().optional(),
    owner_actor_id: z.number().int().nullable().optional(),
  }),
  response: crmOpportunityRowSchema,
  invalidates: [oppList],
};

const get: Operation<
  { id: number },
  void,
  void,
  z.infer<typeof crmOpportunityRowSchema>
> = {
  method: "GET",
  path: ({ id }) => `/api/crm/opportunities/${id}`,
  params: crmIdParams,
  response: crmOpportunityRowSchema,
};

const scopedDetail = scoped(
  get,
  (args: Args<{ id: number }, void, unknown>) => ({ id: args.params.id }),
);

const update: Operation<
  { id: number },
  void,
  {
    name?: string;
    value_minor?: number;
    currency?: string;
    probability_pct?: number | null;
    expected_close_date?: string | null;
    owner_actor_id?: number | null;
  },
  z.infer<typeof crmOpportunityRowSchema>
> = {
  method: "PATCH",
  path: ({ id }) => `/api/crm/opportunities/${id}`,
  params: crmIdParams,
  body: z
    .object({
      name: z.string().min(1).optional(),
      value_minor: z.number().int().optional(),
      currency: z.string().length(3).optional(),
      probability_pct: z.number().int().nullable().optional(),
      expected_close_date: z.string().nullable().optional(),
      owner_actor_id: z.number().int().nullable().optional(),
    })
    .partial(),
  response: crmOpportunityRowSchema,
  invalidates: [oppList, scopedDetail],
};

const moveStage: Operation<
  { id: number },
  void,
  { stage_id: number },
  z.infer<typeof crmOpportunityRowSchema>
> = {
  method: "POST",
  path: ({ id }) => `/api/crm/opportunities/${id}/move_stage`,
  params: crmIdParams,
  body: z.object({ stage_id: z.number().int() }),
  response: crmOpportunityRowSchema,
  // stage filters on the list query family — wildcard whole list.
  invalidates: [oppList, scopedDetail],
};

const win: Operation<
  { id: number },
  void,
  void,
  z.infer<typeof crmOpportunityRowSchema>
> = {
  method: "POST",
  path: ({ id }) => `/api/crm/opportunities/${id}/win`,
  params: crmIdParams,
  response: crmOpportunityRowSchema,
  invalidates: [oppList, scopedDetail],
};

const lose: Operation<
  { id: number },
  void,
  { lost_reason?: string | null },
  z.infer<typeof crmOpportunityRowSchema>
> = {
  method: "POST",
  path: ({ id }) => `/api/crm/opportunities/${id}/lose`,
  params: crmIdParams,
  body: z.object({ lost_reason: z.string().nullable().optional() }),
  response: crmOpportunityRowSchema,
  invalidates: [oppList, scopedDetail],
};

const archive: Operation<
  { id: number },
  void,
  void,
  z.infer<typeof crmOpportunityRowSchema>
> = {
  method: "POST",
  path: ({ id }) => `/api/crm/opportunities/${id}/archive`,
  params: crmIdParams,
  response: crmOpportunityRowSchema,
  invalidates: [oppList, scopedDetail],
};

export const opportunities = {
  list: oppList,
  create,
  get,
  update,
  move_stage: moveStage,
  win,
  lose,
  archive,
};
